import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccessToken } from '@/auth/useAccessToken';
import { cn } from '@/lib/utils';

const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Round profile photo for a directory user, fetched from the API photo proxy
 * with the bearer token (an <img src> can't send auth). Falls back to initials
 * when the user has no photo (404). The blob is cached by oid via TanStack
 * Query so the same avatar isn't refetched across renders / search results.
 */
export function UserAvatar({ oid, name, sizeClass = 'w-8 h-8' }: { oid: string; name: string; sizeClass?: string }) {
  const getAccessToken = useAccessToken();

  const photo = useQuery({
    queryKey: ['user-photo', oid],
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: false,
    queryFn: async ({ signal }): Promise<Blob | null> => {
      const token = await getAccessToken();
      const res = await fetch(`${base}/api/directory/users/${oid}/photo`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`photo ${res.status}`);
      return res.blob();
    }
  });

  const url = useMemo(() => (photo.data ? URL.createObjectURL(photo.data) : null), [photo.data]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-medium overflow-hidden',
        sizeClass
      )}
      title={name}
    >
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : initials(name)}
    </span>
  );
}
